import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import Header from './Header';
import Footer from './Footer';
import { getToolContent } from '../data/toolContent';

interface ToolLayoutProps {
  children: React.ReactNode;
  title: string;
  description: string;
  canonicalPath: string;
}

const SITE = 'https://www.alltools.sbs';

const setMetaByName = (name: string, content: string) => {
  let tag = document.querySelector(`meta[name="${name}"]`);
  if (!tag) {
    tag = document.createElement('meta');
    tag.setAttribute('name', name);
    document.head.appendChild(tag);
  }
  tag.setAttribute('content', content);
};

const setMetaByProperty = (property: string, content: string) => {
  let tag = document.querySelector(`meta[property="${property}"]`);
  if (!tag) {
    tag = document.createElement('meta');
    tag.setAttribute('property', property);
    document.head.appendChild(tag);
  }
  tag.setAttribute('content', content);
};

const ToolLayout: React.FC<ToolLayoutProps> = ({ children, title, description, canonicalPath }) => {
  // toolContent is the single source of truth shared with scripts/prerender.mjs.
  const content = getToolContent(canonicalPath);
  const metaTitle = content?.title || title;
  const metaDescription = content?.description || description;
  const canonicalUrl = `${SITE}${canonicalPath}`;

  useEffect(() => {
    document.title = metaTitle;
    setMetaByName('description', metaDescription);
    if (content?.keywords?.length) setMetaByName('keywords', content.keywords.join(', '));

    let canonical = document.querySelector('link[rel="canonical"]');
    if (!canonical) {
      canonical = document.createElement('link');
      canonical.setAttribute('rel', 'canonical');
      document.head.appendChild(canonical);
    }
    canonical.setAttribute('href', canonicalUrl);

    setMetaByProperty('og:title', metaTitle);
    setMetaByProperty('og:description', metaDescription);
    setMetaByProperty('og:url', canonicalUrl);
    setMetaByProperty('og:type', 'website');
    setMetaByName('twitter:title', metaTitle);
    setMetaByName('twitter:description', metaDescription);
  }, [metaTitle, metaDescription, canonicalUrl, content]);

  return (
    <div className="min-h-screen bg-gray-50">
      <Header />
      <main className="container mx-auto px-4 py-8">
        <div className="max-w-4xl mx-auto">
          {content && (
            <nav aria-label="Breadcrumb" className="text-sm text-gray-500 mb-3">
              <Link to="/" className="hover:text-primary-600">Home</Link>
              <span className="mx-1">›</span>
              <span>{content.category}</span>
              <span className="mx-1">›</span>
              <span className="text-gray-700">{content.name}</span>
            </nav>
          )}
          {content && (
            <header className="mb-6">
              <h1 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-2">{content.h1}</h1>
              <p className="text-gray-600 leading-relaxed">{content.intro}</p>
            </header>
          )}

          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4 sm:p-6 lg:p-8">
            {children}
          </div>

          {content && (
            <div className="mt-10 space-y-10">
              {content.features?.length > 0 && (
                <section>
                  <h2 className="text-xl font-bold text-gray-900 mb-4">Why use this {content.name}?</h2>
                  <ul className="grid sm:grid-cols-2 gap-2 list-disc pl-5 text-gray-700">
                    {content.features.map((f) => <li key={f}>{f}</li>)}
                  </ul>
                </section>
              )}

              {content.howTo?.length > 0 && (
                <section>
                  <h2 className="text-xl font-bold text-gray-900 mb-4">How to use the {content.name}</h2>
                  <ol className="space-y-3 list-decimal pl-5 text-gray-700">
                    {content.howTo.map((s) => (
                      <li key={s.name}><strong className="text-gray-900">{s.name}:</strong> {s.text}</li>
                    ))}
                  </ol>
                </section>
              )}

              {content.faqs?.length > 0 && (
                <section>
                  <h2 className="text-xl font-bold text-gray-900 mb-4">Frequently Asked Questions</h2>
                  <div className="space-y-4">
                    {content.faqs.map((f) => (
                      <div key={f.q} className="bg-white rounded-lg border border-gray-200 p-4">
                        <h3 className="font-semibold text-gray-900 mb-1">{f.q}</h3>
                        <p className="text-gray-700 leading-relaxed">{f.a}</p>
                      </div>
                    ))}
                  </div>
                </section>
              )}

              {content.related?.length > 0 && (
                <section>
                  <h2 className="text-xl font-bold text-gray-900 mb-4">Related tools</h2>
                  <div className="flex flex-wrap gap-2">
                    {content.related.map((r) => {
                      const rel = getToolContent(r);
                      return rel ? (
                        <Link key={r} to={r}
                          className="px-3 py-2 bg-white border border-gray-200 rounded-lg text-sm text-gray-700 hover:border-primary-400 hover:text-primary-600">
                          {rel.name}
                        </Link>
                      ) : null;
                    })}
                  </div>
                </section>
              )}
            </div>
          )}
        </div>
      </main>
      <Footer />
    </div>
  );
};

export default ToolLayout;
